// Pure reducer: (DraftState, ServerEvent) -> DraftState
// Applied by useDraftChannel when events arrive over the cable.

import type {
  DraftState,
  PickState,
  ServerEvent,
} from '../types/protocol'

export function draftReducer(state: DraftState, event: ServerEvent): DraftState {
  switch (event.type) {
    case 'draft_started':
      // Full state replacement. The broadcast is serialized without a team
      // token, so the caller's private fields have to be carried over.
      return { ...event.state, my_team_id: state.my_team_id, my_queue: state.my_queue }

    case 'pick_made': {
      const { pick, on_clock, clock, completed } = event

      // Update the pick in the picks array
      const picks = state.picks.map((p): PickState =>
        p.overall === pick.overall
          ? {
              ...p,
              player_id: pick.player.id,
              status: 'made' as const,
              made_by: pick.made_by as PickState['made_by'],
            }
          : p,
      )

      return {
        ...state,
        state_version: event.state_version,
        picks,
        status: completed ? 'complete' : state.status,
        current_pick_overall: on_clock?.overall ?? null,
        deadline_at: clock.deadline_ms
          ? new Date(clock.deadline_ms).toISOString()
          : null,
        completed_at: completed ? new Date(event.server_now_ms).toISOString() : state.completed_at,
      }
    }

    case 'pick_undone': {
      const { overall, on_clock, clock } = event

      const picks = state.picks.map((p): PickState =>
        p.overall === overall
          ? { ...p, player_id: null, status: 'pending' as const, made_by: null, picked_at: null }
          : p,
      )

      return {
        ...state,
        state_version: event.state_version,
        picks,
        status: 'live',
        current_pick_overall: on_clock?.overall ?? null,
        deadline_at: clock.deadline_ms
          ? new Date(clock.deadline_ms).toISOString()
          : null,
        completed_at: null,
      }
    }

    case 'pick_skipped': {
      const { overall, on_clock, clock } = event

      const picks = state.picks.map((p): PickState =>
        p.overall === overall
          ? { ...p, status: 'skipped' as const, made_by: 'commissioner' }
          : p,
      )

      return {
        ...state,
        state_version: event.state_version,
        picks,
        current_pick_overall: on_clock?.overall ?? null,
        deadline_at: clock.deadline_ms
          ? new Date(clock.deadline_ms).toISOString()
          : null,
      }
    }

    case 'clock_started':
    case 'clock_expired':
    case 'draft_paused':
    case 'draft_resumed': {
      const { on_clock, clock } = event
      const statusMap: Record<string, DraftState['status']> = {
        clock_started: 'live',
        clock_expired: 'live',
        draft_paused: 'paused',
        draft_resumed: 'live',
      }

      return {
        ...state,
        state_version: event.state_version,
        status: statusMap[event.type] ?? state.status,
        current_pick_overall: on_clock?.overall ?? state.current_pick_overall,
        deadline_at: clock.deadline_ms
          ? new Date(clock.deadline_ms).toISOString()
          : null,
        paused_remaining_ms: clock.remaining_ms_hint,
      }
    }

    case 'draft_completed':
      return {
        ...state,
        state_version: event.state_version,
        status: 'complete',
        current_pick_overall: null,
        deadline_at: null,
        completed_at: new Date(event.server_now_ms).toISOString(),
      }

    case 'teams_updated':
      return {
        ...state,
        state_version: event.state_version,
        teams: event.teams,
      }

    case 'settings_updated':
      return {
        ...state,
        state_version: event.state_version,
        name: event.settings.name,
        draft_type: event.settings.draft_type,
        pick_seconds: event.settings.pick_seconds,
        expiry_policy: event.settings.expiry_policy,
        roster_config: event.settings.roster_config,
      }

    case 'order_randomized': {
      const teams = state.teams.map((t) => {
        const entry = event.order.find((o) => o.id === t.id)
        return entry ? { ...t, draft_position: entry.draft_position } : t
      })
      // Re-sort by draft_position to match server order
      teams.sort((a, b) => (a.draft_position ?? 0) - (b.draft_position ?? 0))
      return {
        ...state,
        state_version: event.state_version,
        teams,
      }
    }

    case 'lottery_scheduled':
      return {
        ...state,
        state_version: event.state_version,
        lottery_at: event.lottery_at,
      }

    case 'lottery_revealed':
      // Full state replacement, the draft_started shape. The broadcast is
      // serialized without a team token, so my_team_id and my_queue are absent
      // from the payload and have to be carried over.
      //
      // Deliberately not a hand-patched `teams` array: the whole reason this
      // event ships full state is that the reducer's output and a plain
      // refetch's output have to be the same object. draftSync refetches and
      // *returns* on any version gap, so a dropped reveal is recovered by that
      // refetch alone — and the two paths must not be able to disagree.
      return {
        ...event.state,
        state_version: event.state_version,
        my_team_id: state.my_team_id,
        my_queue: state.my_queue,
      }

    case 'rankings_imported':
      // The reducer just bumps the version — the provider will refetch
      // the player pool when it sees this event type.
      return {
        ...state,
        state_version: event.state_version,
      }

    default:
      return state
  }
}
